import {
  createRootRouteWithContext,
  Link,
  Outlet,
  redirect,
  retainSearchParams,
} from '@tanstack/react-router'
import { Compass } from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import { readStorage } from '@/lib/storage'
import type { RouterContext } from '@/router'
import { AppShell } from '@/shell/AppShell'
import { APP_SCOPE_STORAGE_KEY } from '@/shell/useAppScope'

/** Search params every page shares. `app` is the global application scope. */
export interface RootSearch {
  app?: string
}

export const Route = createRootRouteWithContext<RouterContext>()({
  validateSearch: (search: Record<string, unknown>): RootSearch => ({
    app: typeof search.app === 'string' && search.app !== '' ? search.app : undefined,
  }),
  search: { middlewares: [retainSearchParams(['app'])] },
  beforeLoad: ({ search, location }) => {
    // Restore the remembered scope on a fresh visit without ?app=.
    if (!search.app) {
      const remembered = readStorage(APP_SCOPE_STORAGE_KEY)
      if (remembered) {
        throw redirect({
          to: location.pathname,
          search: { ...search, app: remembered },
          replace: true,
        })
      }
    }
  },
  component: RootLayout,
  notFoundComponent: NotFound,
})

function RootLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}

function NotFound() {
  return (
    <EmptyState
      icon={Compass}
      title="This page doesn’t exist"
      description="The link may be out of date, or the item was deleted."
      action={
        <Button asChild variant="secondary">
          <Link to="/">Go to the dashboard</Link>
        </Button>
      }
    />
  )
}
